import { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { InfoBox, Panel, ReactTable, BrowserIcon, OSIcon, SkeletonLine, Tooltip, capitalize } from '@crazyegginc/hatch';
import { SearchInput } from '/src/components/search-input';
import classNames from 'classnames';
import ReactCountryFlag from 'react-country-flag';
import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';
import { constantizeString, formatBrowserName, humanizeMetric, percentizeValue } from '../utils';
import { msToHumanDuration } from '/src/utils/date';
import { ReactComponent as LinkIcon } from '@crazyegginc/hatch/dist/images/icon-open-url-outline.svg';
import { ReactComponent as NoResultsIcon } from '@crazyegginc/hatch/dist/images/icon-no-results-outline.svg';
import { useQueryParams } from '/src/hooks';
import { useAtom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';

import { SupportLinks } from '/src/support';
import { RowColor } from './row-color';
import { capitalizeFirstLetter } from '/src/utils/string';

import { SORT_ORDER_TYPES } from '../../_global/constants';
import { clampNumber } from '../../_global/utils';
import { DIMENSIONS, METRICS, VALID_OVERTIME_METRICS } from '../constants';
import { Favicon } from './Favicon';

import { InstantHeatmaps } from '/src/features/web-analytics/components/InstantHeatmaps';
import { SOURCE_TYPE, useSelectedSource } from '/src/components/source-selector/source-selector';

function ShowTooltip({ type = 'block', text, link }) {
  const ref = useRef();
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    setShowTooltip(ref.current?.clientWidth < ref.current?.scrollWidth);

    const getTruncated = () => {
      setShowTooltip(ref.current?.clientWidth < ref.current?.scrollWidth);
    };
    window.addEventListener('resize', getTruncated);

    return () => window.removeEventListener('resize', getTruncated);
  }, []);

  if (type === 'link') {
    return (
      <div className="w-full relative flex items-center">
        <div className="w-full overflow-hidden mr-3" ref={ref}>
          <Tooltip
            show={showTooltip}
            tooltipContent={<div className="w-[370px] text-left leading-none break-words">{text}</div>}
          >
            <a href={`https://${link}`} target="_blank" className="flex items-center space-x-2" rel="noreferrer">
              <span className="whitespace-nowrap truncate">{text}</span>
              {!showTooltip && (
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <LinkIcon className="flex-shrink-0 fill-current h-3 w-3 opacity-50" aria-label="external" />
                </div>
              )}
            </a>
          </Tooltip>
        </div>
        {showTooltip && (
          <div className="absolute top-1/2 -translate-y-1/2 -right-8 w-7 flex items-start">
            &hellip;{' '}
            <LinkIcon className="flex-shrink-0 fill-current h-3 w-3 opacity-50 mt-1 ml-1" aria-label="external" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full relative flex items-center">
      <div className="w-full overflow-hidden mr-3" ref={ref}>
        <Tooltip
          show={showTooltip}
          tooltipContent={<div className="w-[370px] text-left leading-none break-words">{text}</div>}
        >
          <div className="w-full z-0">{text}</div>
        </Tooltip>
      </div>
      {showTooltip && <div className="absolute top-1/2 -translate-y-1/2 -right-3">&hellip;</div>}
    </div>
  );
}

const linkCell = ({ selectedSite, path, domain = null }) => {
  const showDomain =
    (domain && domain.length > 0 && domain !== selectedSite?.name) || (domain?.length === 0 && path.length === 0);

  if (path.includes('*')) {
    return <ShowTooltip text={`${showDomain ? domain : ''}${path}`} />;
  }

  if (domain && domain === '(other)') {
    return (
      <div className="w-full relative flex items-center">
        <span>(other)</span>
        <div className="ml-2 mt-[3px]">
          <Tooltip
            tooltipContent={
              <div className="max-w-[190px]">
                Only the top 2,000 values are imported. All remaining values are grouped into “(other)”
              </div>
            }
          >
            <InfoIcon className="h-3 w-3 fill-current flex-shrink-0 opacity-50" aria-label="hover for more info" />
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <ShowTooltip
      type="link"
      text={`${showDomain ? domain : ''}${path}`}
      link={`${domain || (selectedSite?.name ?? '')}${path}`}
    />
  );
};

const LabelColumnContent = ({ text }) => {
  return <ShowTooltip text={text} />;
};

const labelColumn = (hasNoResults, header, accessorKey = 'page', cellComponent = null, size = 120, orderMeta = {}) => {
  return {
    header: () => (
      <>
        <span className="flex items-center">
          {header}{' '}
          {header.toLowerCase() === 'page' && (
            <Tooltip
              className="flex-shrink-0"
              tooltipContent={
                <span>
                  Crazy Egg automatically groups similar URLs
                  <br />
                  that have ID-like patterns together.
                </span>
              }
            >
              <InfoIcon
                className="h-3 w-3 fill-current flex-shrink-0 opacity-50 ml-2"
                aria-label="hover for more info"
              />
            </Tooltip>
          )}
        </span>
      </>
    ),
    accessorKey,
    size,
    meta: {
      align: 'center',
      justify: 'left',
      headerCellClassnames: classNames('!pl-0 !normal-case !text-sm !text-black-pearl-500 !font-semibold', {
        '!opacity-20': hasNoResults,
        '!opacity-100': !hasNoResults,
      }),
      rowCellClassnames: '!pl-0',
    },
    cell: cellComponent
      ? ({ row }) => {
          return (
            <span className="flex h-full space-x-4 w-full">
              <RowColor row={row} orderMeta={orderMeta} />
              {cellComponent({ row })}
            </span>
          );
        }
      : function Cell({ row }) {
          return (
            <span className="flex h-full space-x-4 items-center w-full">
              <RowColor row={row} orderMeta={orderMeta} />
              <LabelColumnContent
                text={
                  header.toLowerCase() === 'device' ? capitalize(row.original[accessorKey]) : row.original[accessorKey]
                }
              />
            </span>
          );
        },
  };
};

const simpleColumn = (hasNoResults, accessorKey, mutator = null, size = 70) => {
  const header = humanizeMetric(accessorKey);
  return {
    header,
    accessorKey,
    size,
    meta: {
      align: 'center',
      justify: 'right',
      headerCellClassnames: classNames('!normal-case !text-sm !text-black-pearl-500 !font-semibold', {
        '!opacity-20': hasNoResults,
        '!opacity-100': !hasNoResults,
      }),
    },
    cell: function Cell({ row }) {
      if (mutator) {
        return mutator?.(row.original[accessorKey]);
      }
      return row.original[accessorKey];
    },
  };
};

const instantHeatmapColumn = (hasNoResults, selectedSource, size = 35, className = '') => {
  if (selectedSource?.type === SOURCE_TYPE.GA_PROPERTY) {
    // Don't show Instant Heatmaps for GA properties for now:
    // https://app.shortcut.com/crazyegg/story/70122/remove-instant-heatmaps-modal-fire-icon-on-ga-properties
    return null;
  }

  return {
    header: 'Instant Heatmap',
    accessorKey: 'instantHeatmap',
    size,
    enableSorting: false,
    meta: {
      align: 'center',
      justify: 'center',
      headerCellClassnames: classNames('!normal-case !text-sm !text-black-pearl-500 !font-semibold', {
        '!opacity-20': hasNoResults,
        '!opacity-100': !hasNoResults,
      }),
      rowCellClassnames: className,
    },
    cell: function Cell({ row }) {
      return (
        <div>
          <InstantHeatmaps path={row.original.name} domain={row.original.domain} selectedSource={selectedSource} />
        </div>
      );
    },
  };
};

function numberFormatter(value) {
  return value.toLocaleString();
}

const bounceRateColumn = (hasNoResults, size = 50) => {
  return simpleColumn(
    hasNoResults,
    'bounceRate',
    (value) => {
      const safeValue = clampNumber(value, 0, 1);
      return percentizeValue(safeValue, '-');
    },
    size,
  );
};

const durationMutator = (value) => (value ? msToHumanDuration(value, false, true) : '-');

const categoricalMetricColumns = (hasNoResults, title, customLabelComponent, orderMeta = {}) => [
  labelColumn(hasNoResults, title, 'name', customLabelComponent, 150, orderMeta),
  simpleColumn(hasNoResults, 'visitors', numberFormatter, 35),
  simpleColumn(hasNoResults, 'pageViews', numberFormatter, 45),
  simpleColumn(hasNoResults, 'sessions', numberFormatter, 40),
  simpleColumn(hasNoResults, 'avgSessionDuration', durationMutator),
  bounceRateColumn(hasNoResults),
];

function browserCellLabel({ row }) {
  const { name } = row.original;

  return (
    <div className="flex items-center space-x-2 truncate">
      <BrowserIcon className="w-4 h-4" browser={constantizeString(name)} variant="COLOR" renderUnknown={true} />
      <span className="mt-0.5 truncate">{formatBrowserName(name)}</span>
    </div>
  );
}

function osCellLabel({ row }) {
  const { name } = row.original;

  return (
    <div className="flex items-center space-x-2 truncate">
      <OSIcon className="w-4 h-4" os={constantizeString(name)} variant="COLOR" renderUnknown={true} />
      <span className="mt-0.5 truncate">{name}</span>
    </div>
  );
}

function languageCellLabel({ row }) {
  return (
    <div className="flex items-center space-x-2 truncate">
      <span className="mt-0.5 truncate">{row.original.name}</span>
    </div>
  );
}

function getPlaceholder(view) {
  switch (view) {
    case 'deviceType':
      return 'Search Devices';
    case 'os':
      return 'Search Operating Systems';
    case 'pages':
    case 'entryPages':
      return 'Search Pages';
    case 'utmMediums':
      return 'Search UTM Medium';
    case 'utmContents':
      return 'Search UTM Content';
    case 'utmTerms':
      return 'Search UTM Terms';
    case 'utmCampaigns':
      return 'Search UTM Campaigns';
    case 'utmSources':
      return 'Search UTM Source';
    default:
      return `Search ${capitalizeFirstLetter(view)}`;
  }
}

export function getDefaultOrder(overTime = false) {
  const params = new URLSearchParams(window.location.search);
  const view = window.location.pathname.split('/').at(-1);

  const orderColumn = params.get('order');
  const sortDirection = params.get('sort');

  const defaultColumn = view === DIMENSIONS.PAGES ? METRICS.PAGE_VIEWS : METRICS.VISITORS;

  const hasOrderColumn = !!orderColumn && orderColumn !== 'undefined';
  const hasSortOrder = !!sortDirection && sortDirection !== 'undefined';

  const desc = hasSortOrder ? sortDirection.toUpperCase() === SORT_ORDER_TYPES.DESC : true;

  if (overTime) {
    const fallbackColumn = METRICS.VISITORS;
    const validOrderColumn = hasOrderColumn
      ? VALID_OVERTIME_METRICS.includes(orderColumn)
        ? orderColumn
        : fallbackColumn
      : defaultColumn;

    return { id: validOrderColumn, desc };
  }

  return {
    id: hasOrderColumn ? orderColumn : defaultColumn,
    desc,
  };
}

export const columnOrderAtom = atomWithDefault(() => getDefaultOrder());

export const lastValidOrderAtom = atomWithDefault(() => getDefaultOrder(true).id);

export const searchAtom = atomWithDefault(() => {
  const params = new URLSearchParams(window.location.search);
  return params.get('search') || '';
});

export function AnalyticsTable({
  selected = null,
  analytics = [],
  totalCount = 0,
  title,
  isLoading = false,
  hasNextPage = false,
  fetchNextPage = null,
  orderMeta = {},
}) {
  const { selectedSource } = useSelectedSource();
  const selectedSite = selectedSource.type === SOURCE_TYPE.SITE ? selectedSource : null;
  const tableRef = useRef(null);
  const { set: paramSet, setAll: paramSetAll, remove: paramRemove } = useQueryParams();
  const [sortOrder, setSortOrder] = useAtom(columnOrderAtom);
  const [, setLastValidOrder] = useAtom(lastValidOrderAtom);
  const [search, setSearch] = useAtom(searchAtom);
  const hasNoResults = !isLoading && totalCount === 0;

  const columns = useMemo(() => {
    switch (selected) {
      case 'deviceType':
        return categoricalMetricColumns(hasNoResults, 'Device', null, orderMeta);
      case 'countries':
        return [
          labelColumn(
            hasNoResults,
            'Country',
            'name',
            function CountryTitle({ row }) {
              return (
                <div className="flex items-center space-x-2 truncate">
                  <ReactCountryFlag
                    countryCode={row.original.code}
                    svg
                    alt={row.original.name}
                    style={{
                      width: '18px',
                      height: '18px',
                      border: '1px solid #e1e6ef',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      overflow: 'hidden',
                    }}
                  />
                  <span className="mt-0.5 truncate">{row.original.name}</span>
                </div>
              );
            },
            150,
            orderMeta,
          ),
          simpleColumn(hasNoResults, 'visitors', numberFormatter, 45),
          simpleColumn(hasNoResults, 'pageViews', numberFormatter, 45),
          simpleColumn(hasNoResults, 'sessions', numberFormatter, 45),
          simpleColumn(hasNoResults, 'avgSessionDuration', durationMutator),
          bounceRateColumn(hasNoResults),
        ];
      case 'languages':
        return categoricalMetricColumns(hasNoResults, 'Language', languageCellLabel, orderMeta);
      case 'os':
        return categoricalMetricColumns(hasNoResults, 'OS', osCellLabel, orderMeta);
      case 'browsers':
        return categoricalMetricColumns(hasNoResults, 'Browser', browserCellLabel, orderMeta);
      case 'channels':
        return categoricalMetricColumns(hasNoResults, 'Channel', null, orderMeta);
      case 'referrers':
        return categoricalMetricColumns(
          hasNoResults,
          'Referrer',
          function ReferrerTitle({ row }) {
            return (
              <div className="flex items-center truncate">
                <Favicon domain={row.original.favicon} className="mr-2" />
                {row.original.domain !== row.original.name ? (
                  <Tooltip placement="bottom" tooltipContent={row.original.domain}>
                    <span className="mt-0.5 truncate">{row.original.name}</span>
                  </Tooltip>
                ) : (
                  <span className="mt-0.5 truncate">{row.original.name}</span>
                )}
              </div>
            );
          },
          orderMeta,
        );
      case 'pages':
        return [
          labelColumn(
            hasNoResults,
            'Page',
            'name',
            ({ row }) => linkCell({ path: row.original.name, domain: row.original.domain, selectedSite }),
            180,
            orderMeta,
          ),
          instantHeatmapColumn(hasNoResults, selectedSource, 35, '!pl-2'),
          simpleColumn(hasNoResults, 'pageViews', numberFormatter, 35),
          simpleColumn(hasNoResults, 'avgTimeOnPage', durationMutator, 40),
        ].filter(Boolean);
      case 'entryPages':
        return [
          labelColumn(
            hasNoResults,
            'Page',
            'name',
            ({ row }) => linkCell({ path: row.original.name, domain: row.original.domain, selectedSite }),
            150,
            orderMeta,
          ),
          instantHeatmapColumn(hasNoResults, selectedSource, 60, '!pl-5'),
          simpleColumn(hasNoResults, 'visitors', numberFormatter, 35),
          simpleColumn(hasNoResults, 'pageViews', numberFormatter, 40),
          simpleColumn(hasNoResults, 'avgTimeOnPage', durationMutator, 60),
          simpleColumn(hasNoResults, 'sessions', numberFormatter, 35),
          simpleColumn(hasNoResults, 'avgSessionDuration', durationMutator, 65),
          bounceRateColumn(hasNoResults, 42),
        ].filter(Boolean);
      default:
        return [
          labelColumn(hasNoResults, 'Name', 'name', null, 150, orderMeta),
          simpleColumn(hasNoResults, 'visitors', numberFormatter, 35),
          simpleColumn(hasNoResults, 'pageViews', numberFormatter, 45),
          simpleColumn(hasNoResults, 'avgTimeOnPage', durationMutator, 65),
          simpleColumn(hasNoResults, 'sessions', numberFormatter, 40),
          simpleColumn(hasNoResults, 'avgSessionDuration', durationMutator),
          bounceRateColumn(hasNoResults),
        ];
    }
  }, [selected, selectedSite, selectedSource, hasNoResults, orderMeta]);

  const searchFunc = useCallback(
    (value) => {
      const parsedValue = (value || '').trim();
      if (parsedValue.length > 0) {
        paramSet('search', encodeURIComponent(parsedValue));
        setSearch(parsedValue);
      } else {
        paramRemove('search');
        setSearch('');
      }
    },
    [paramSet, paramRemove, setSearch],
  );

  const onFetchData = useCallback(
    ({ sorting = null }) => {
      if (sorting) {
        const nextField = sorting[0].id;
        const nextSort = sorting[0].desc ? 'desc' : 'asc';
        paramSetAll({ order: nextField, sort: nextSort });
        setSortOrder({
          id: nextField,
          desc: nextSort === 'desc',
        });
        if (VALID_OVERTIME_METRICS.includes(nextField)) {
          setLastValidOrder(nextField);
        }
      }
    },
    [setSortOrder, paramSetAll, setLastValidOrder],
  );

  return (
    <Panel className={classNames('!p-0', { 'min-h-[314px]': hasNoResults })}>
      <div className="flex items-center justify-between w-full px-5 py-5 flex-shrink-0">
        <div className="flex items-center space-x-[30px]">
          <div className="flex items-center space-x-1">
            <h1 className="text-header-2">{title}</h1>
            {isLoading ? (
              <span className="w-20">
                <SkeletonLine />
              </span>
            ) : (
              <span className="text-xl font-semibold text-charcoal-gray-500 text-opacity-50">{totalCount}</span>
            )}
          </div>
          {selected === 'referrers' ? (
            <InfoBox
              className="flex !items-center text-cadet-blue-500"
              Icon={<InfoIcon className="w-3.5 h-3.5 fill-current" />}
              body={
                <span className="!pt-0.75 leading-tight">
                  Why don&apos;t my subdomains show up as referrers?{' '}
                  <a
                    className="text-link text-2xs"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={SupportLinks.webAnalytics.referrers}
                  >
                    Learn more
                  </a>
                </span>
              }
            />
          ) : null}
        </div>
        <div className="flex items-center space-x-2">
          {/* <Button variant="secondary" className="flex-shrink-0 !h-10"> */}
          {/*   Download CSV */}
          {/* </Button> */}
          <SearchInput
            initialValue={search}
            placeholder={getPlaceholder(selected)}
            search={searchFunc}
            className="blue-text !h-10 !bg-solitude-500 !border-solitude-500 hover:border-dodger-blue-500 focus:border-dodger-blue-500 placeholder-dodger-blue-500"
          />
        </div>
      </div>
      <div className="w-full flex-1">
        <div className="z-10">
          <ReactTable
            fetching={isLoading}
            ref={{ tableRef }}
            rowPadding={false}
            columns={columns}
            data={analytics || []}
            onFetchData={onFetchData}
            enableSorting={true}
            sorting={sortOrder}
            useVirtualization={true}
            className="!border-none !shadow-none"
            headerCustomClassname="!pl-5 pr-5 !text-header-6 !border-[#979797] !border-opacity-20"
            rowCustomClassname={() =>
              classNames(
                'pr-5 odd:bg-[#FAFAFA] after:!border-[#EAEAEA] after:!border-solid after:!left-0 after:!right-0',
              )
            }
            hasNextPage={hasNextPage}
            loadNextPage={fetchNextPage}
            rowHeight={50}
          />
        </div>
        {hasNoResults ? (
          <div className="w-full h-full top-0 left-0 z-10 bg-white bg-opacity-60 flex justify-center items-end">
            <div className="flex flex-col items-center mb-[65px]">
              <NoResultsIcon className="w-8 h-8 opacity-50 mb-2.5" />
              {search && search !== '' ? (
                <span className="text-body-2">
                  Zero results for <strong className="text-body-1">&ldquo;{search}&rdquo;</strong>.
                </span>
              ) : (
                <span className="text-body-2">Zero results for this date range.</span>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
